import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Calendar as CalIcon, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { notify } from "../lib/notify";
import ShareCard from "../components/ShareCard";

function ToolHero({ overline, title, sub }) {
  return (
    <section className="border-b border-slate-200">
      <div className="container-rp py-12 lg:py-16">
        <div className="overline">{overline}</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold mt-3 tracking-tight">{title}</h1>
        {sub && <p className="text-slate-600 mt-4 max-w-2xl text-lg">{sub}</p>}
      </div>
    </section>
  );
}

// ============ PDPA Scorer (10-Q) ============
export function PDPAScorer() {
  const { user } = useAuth();
  const [qs, setQs] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/pdpa-scorer/questions").then(r => setQs(r.data.questions)); }, []);

  const set = (id, v) => setAnswers(prev => ({ ...prev, [id]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/pdpa-scorer/score", { answers });
      setResult(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!user) { notify.info("Sign in to save."); return; }
    try {
      await api.post("/assessments", { type: "pdpa", title: `PDPA ${result.score}/100 · ${result.band}`, input: { answers }, result });
      notify.success("Saved to your account.");
    } catch { notify.error("Could not save."); }
  };

  const colorMap = { emerald: "var(--rp-primary)", amber: "#F59E0B", rose: "#EF4444" };

  if (result) {
    return (
      <div data-testid="tool-pdpa-scorer-result">
        <ToolHero overline="PDPA Compliance Readiness" title={result.headline} sub={`Your composite score: ${result.score}/100 · ${result.band}`} />
        <section className="section">
          <div className="container-rp grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <div className="rp-card p-8">
                <div className="overline">Breakdown by obligation</div>
                <div className="mt-6 space-y-3">
                  {result.breakdown.map(b => (
                    <div key={b.obligation}>
                      <div className="flex items-center justify-between text-sm">
                        <span>{b.obligation}</span>
                        <span className="font-mono">{b.earned}/{b.weight}</span>
                      </div>
                      <div className="h-2 mt-1.5 rp-surface-subtle">
                        <div className="h-full" style={{ width: `${(b.earned / b.weight) * 100}%`, background: colorMap[result.color] }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <aside className="lg:col-span-5">
              <div className="rp-card p-8">
                <div className="overline">Top remediation actions</div>
                <ul className="mt-5 space-y-3">
                  {result.remediation.map((r, i) => (
                    <li key={r} className="text-sm rp-surface-subtle p-3 rounded-sm border border-slate-200">{i + 1}. {r}</li>
                  ))}
                </ul>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button onClick={() => { setResult(null); setAnswers({}); }} className="btn-outline" data-testid="pdpa-scorer-retake">Retake</button>
                  <Link to="/tools/mas-trm-readiness" className="btn-accent">MAS TRM heat-map <ArrowRight size={14} /></Link>
                </div>
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <div className="overline-muted mb-3">Share your score</div>
                  <ShareCard
                    title="PDPA Compliance Readiness"
                    subtitle="Singapore PDPA 2012 + 2020 amendment"
                    score={result.score}
                    band={result.band}
                    color={result.color}
                    extras={result.breakdown.map(b => ({ label: b.obligation, value: `${b.earned}/${b.weight}` }))}
                    onSave={user ? save : null}
                  />
                  {!user && <p className="text-[11px] text-slate-500 mt-3"><Link to="/login" className="font-medium" style={{ color: "var(--rp-accent)" }}>Sign in</Link> to save.</p>}
                </div>
              </div>
            </aside>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="tool-pdpa-scorer">
      <ToolHero overline="PDPA Compliance Readiness Scorer" title="Score yourself across all 9 PDPA obligations." sub="Answer 9 questions honestly. We compute a weighted score and surface the top 3 remediation actions." />
      <section className="section">
        <div className="container-rp max-w-4xl">
          <div className="space-y-4">
            {qs.map((q, i) => (
              <div key={q.id} className="rp-card p-6" data-testid={`pdpa-q-${q.id}`}>
                <div className="overline-muted">Q{i + 1} · {q.obligation} · weight {q.weight}</div>
                <div className="text-base font-medium mt-2">{q.q}</div>
                <div className="mt-4 grid grid-cols-4 gap-2">
                  {["No", "Partial", "Mostly", "Yes"].map((label, idx) => (
                    <button
                      key={label}
                      onClick={() => set(q.id, idx)}
                      data-active={answers[q.id] === idx}
                      className="persona-btn justify-center"
                      data-testid={`pdpa-${q.id}-${idx}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex items-center gap-3">
            <button onClick={submit} disabled={busy || Object.keys(answers).length < qs.length} className="btn-accent disabled:opacity-40" data-testid="pdpa-scorer-submit">
              {busy ? "Scoring…" : `Calculate my PDPA score (${Object.keys(answers).length}/${qs.length})`} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

// ============ MAS TRM Heat-map ============
export function MASTRMReadiness() {
  const { user } = useAuth();
  const [domains, setDomains] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/mas-trm/domains").then(r => setDomains(r.data.domains)); }, []);

  const submit = async () => {
    const r = await api.post("/mas-trm/score", { answers });
    setResult(r.data);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!user) { notify.info("Sign in to save."); return; }
    try {
      await api.post("/assessments", { type: "mas-trm", title: `MAS TRM ${result.average}/100`, input: { answers }, result });
      notify.success("Saved.");
    } catch { notify.error("Could not save."); }
  };

  const heatMap = { green: "#10B981", amber: "#F59E0B", red: "#EF4444" };

  if (result) {
    return (
      <div data-testid="tool-mas-trm-result">
        <ToolHero overline="MAS TRM Readiness" title={`Average maturity ${result.average}/100`} sub={result.summary} />
        <section className="section">
          <div className="container-rp">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
              {result.heatmap.map(d => (
                <div key={d.id} className="rp-surface p-6">
                  <div className="overline-muted">{d.section} · {d.name}</div>
                  <div className="mt-4 flex items-baseline gap-3">
                    <div className="font-display text-4xl font-bold" style={{ color: heatMap[d.heat] }}>{d.score}</div>
                    <div className="font-mono text-xs text-slate-500">/100</div>
                  </div>
                  <div className="mt-3 h-2 rp-surface-subtle">
                    <div className="h-full" style={{ width: `${d.score}%`, background: heatMap[d.heat] }} />
                  </div>
                  <div className="text-xs text-slate-500 mt-3 leading-relaxed">{d.q}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex gap-3 flex-wrap">
              <button onClick={() => { setResult(null); setAnswers({}); }} className="btn-outline">Retake</button>
              <Link to="/tools/csrs-calculator" className="btn-accent">Roll-up into CSRS <ArrowRight size={14} /></Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="tool-mas-trm">
      <ToolHero overline="MAS TRM Readiness Check" title="Score each MAS TRM domain." sub="Nine MAS TRM control domains. Mark each (0=None / 1=Partial / 2=Mostly / 3=Fully) and we'll surface a heat-map." />
      <section className="section">
        <div className="container-rp max-w-4xl space-y-4">
          {domains.map((d, i) => (
            <div key={d.id} className="rp-card p-6">
              <div className="overline-muted">Domain {i + 1} · MAS TRM {d.section}</div>
              <div className="font-display text-lg font-semibold mt-1">{d.name}</div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{d.q}</p>
              <div className="mt-4 grid grid-cols-4 gap-2">
                {["None", "Partial", "Mostly", "Fully"].map((lbl, idx) => (
                  <button key={lbl} onClick={() => setAnswers(prev => ({ ...prev, [d.id]: idx }))} data-active={answers[d.id] === idx} className="persona-btn justify-center" data-testid={`mtrm-${d.id}-${idx}`}>{lbl}</button>
                ))}
              </div>
            </div>
          ))}
          <button onClick={submit} disabled={Object.keys(answers).length < domains.length} className="btn-accent disabled:opacity-40" data-testid="mtrm-submit">
            Render heat-map ({Object.keys(answers).length}/{domains.length}) <ArrowRight size={14} />
          </button>
        </div>
      </section>
    </div>
  );
}

// ============ AI Risk Maturity (Veritas FEAT) ============
export function AIRiskMaturity() {
  const { user } = useAuth();
  const [domains, setDomains] = useState([]);
  const [levels, setLevels] = useState({});
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/ai-maturity/domains").then(r => { setDomains(r.data.domains); setLevels(r.data.levels); });
  }, []);

  const submit = async () => {
    const r = await api.post("/ai-maturity/score", { answers });
    setResult(r.data);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!user) { notify.info("Sign in to save."); return; }
    try {
      await api.post("/assessments", { type: "ai-maturity", title: `AI Maturity L${result.overall_level} · ${result.level_name}`, input: { answers }, result });
      notify.success("Saved.");
    } catch { notify.error("Could not save."); }
  };

  if (result) {
    const colorMap = { emerald: "#10B981", amber: "#F59E0B", rose: "#EF4444" };
    return (
      <div data-testid="tool-ai-maturity-result">
        <ToolHero overline="AI Risk Maturity · Veritas FEAT" title={`Level ${result.overall_level} · ${result.level_name}`} sub={result.level_desc} />
        <section className="section">
          <div className="container-rp">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
              {result.domains.map(d => (
                <div key={d.id} className="rp-surface p-6">
                  <div className="overline-muted">{d.principle} · Level {d.level}</div>
                  <div className="font-display text-lg font-semibold mt-2">{d.name}</div>
                  <div className="mt-3 flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map(lvl => (
                      <div key={lvl} className="h-2 flex-1" style={{ background: lvl <= d.level ? colorMap[levels[d.level]?.color || "amber"] : "var(--rp-border)" }} />
                    ))}
                  </div>
                  <div className="text-xs text-slate-500 mt-2">{d.level_name}</div>
                </div>
              ))}
            </div>
            {result.feat_gaps.length > 0 && (
              <div className="rp-card p-8 mt-10">
                <div className="overline">FEAT gap report</div>
                <h3 className="font-display text-2xl font-semibold mt-3">Domains below maturity Level 3</h3>
                <ul className="mt-4 space-y-2">
                  {result.feat_gaps.map((g) => (
                    <li key={g.domain} className="rp-surface-subtle p-3 border border-slate-200 rounded-sm text-sm flex items-center justify-between">
                      <span>{g.domain}</span>
                      <span className="font-mono text-xs">Level {g.level} · {g.level_name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => { setResult(null); setAnswers({}); }} className="btn-outline">Retake</button>
              <Link to="/singapore/mindforge" className="btn-accent">Open MindForge deep-dive <ArrowRight size={14} /></Link>
            </div>
            <div className="mt-10">
              <div className="overline-muted mb-3">Share your maturity</div>
              <ShareCard
                title="AI Risk Maturity"
                subtitle="Veritas FEAT · MindForge"
                score={result.overall_level}
                band={`Level ${result.overall_level} · ${result.level_name}`}
                color={result.color}
                extras={result.domains.map(d => ({ label: `${d.principle} ${d.name}`, value: `L${d.level}` }))}
                onSave={user ? save : null}
              />
              {!user && <p className="text-[11px] text-slate-500 mt-3"><Link to="/login" className="font-medium" style={{ color: "var(--rp-accent)" }}>Sign in</Link> to save.</p>}
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="tool-ai-maturity">
      <ToolHero overline="AI Risk Maturity · Veritas FEAT" title="Rate your AI risk maturity across 8 domains." sub="MAS MindForge / Veritas FEAT aligned. Pick Level 1 (Initial) to Level 5 (Optimising) for each domain." />
      <section className="section">
        <div className="container-rp max-w-4xl space-y-4">
          {domains.map((d, i) => (
            <div key={d.id} className="rp-card p-6">
              <div className="overline-muted">Domain {i + 1} · {d.principle}</div>
              <div className="font-display text-lg font-semibold mt-1">{d.name}</div>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">{d.q}</p>
              <div className="mt-4 grid grid-cols-5 gap-2">
                {[1, 2, 3, 4, 5].map(lvl => (
                  <button key={lvl} onClick={() => setAnswers(prev => ({ ...prev, [d.id]: lvl }))} data-active={answers[d.id] === lvl} className="persona-btn justify-center flex-col py-3" data-testid={`ai-${d.id}-${lvl}`}>
                    <span className="font-mono text-xs">L{lvl}</span>
                    <span className="text-[10px]">{levels[lvl]?.name}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          <button onClick={submit} disabled={Object.keys(answers).length < domains.length} className="btn-accent disabled:opacity-40" data-testid="ai-submit">
            Generate maturity report ({Object.keys(answers).length}/{domains.length}) <ArrowRight size={14} />
          </button>
        </div>
      </section>
    </div>
  );
}

// ============ Non-Compliance Cost Calculator ============
export function NonComplianceCost() {
  const [regimes, setRegimes] = useState([]);
  const [selected, setSelected] = useState([]);
  const [turnover, setTurnover] = useState(50_000_000);
  const [severity, setSeverity] = useState("medium");
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/non-compliance-cost/regimes").then(r => setRegimes(r.data.regimes)); }, []);

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const submit = async () => {
    const r = await api.post("/non-compliance-cost/calculate", { annual_turnover_sgd: turnover, selected_regimes: selected, severity });
    setResult(r.data);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const fmt = (n) => "S$" + n.toLocaleString();

  return (
    <div data-testid="tool-cost">
      <ToolHero overline="Cost-of-Non-Compliance Calculator" title="Estimate your S$ penalty exposure." sub="Select the regimes that could apply, set your turnover and a severity assumption." />
      <section className="section">
        <div className="container-rp grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-6">
            <div className="rp-card p-6">
              <div className="overline">1 · Your annual turnover</div>
              <div className="mt-3 flex items-center gap-3">
                <input type="range" min="1000000" max="5000000000" step="1000000" value={turnover} onChange={(e) => setTurnover(Number(e.target.value))} className="flex-1" data-testid="cost-turnover" />
                <div className="font-mono text-base font-semibold w-44 text-right">{fmt(turnover)}</div>
              </div>
            </div>
            <div className="rp-card p-6">
              <div className="overline">2 · Severity</div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {[["low", "Low (0.15×)"], ["medium", "Medium (0.5×)"], ["high", "High (1.0×)"]].map(([k, l]) => (
                  <button key={k} onClick={() => setSeverity(k)} data-active={severity === k} className="persona-btn justify-center" data-testid={`cost-sev-${k}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="rp-card p-6">
              <div className="overline">3 · Applicable regimes</div>
              <div className="mt-4 space-y-2">
                {regimes.map(r => (
                  <label key={r.id} className="flex items-start gap-3 p-3 border border-slate-200 rounded-sm cursor-pointer hover:border-slate-400" data-testid={`cost-regime-${r.id}`}>
                    <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} className="mt-1" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{r.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{r.basis}</div>
                    </div>
                    <div className="font-mono text-xs text-slate-500">{fmt(r.max_sgd)}</div>
                  </label>
                ))}
              </div>
            </div>
            <button onClick={submit} disabled={selected.length === 0} className="btn-accent disabled:opacity-40" data-testid="cost-submit">Calculate exposure <ArrowRight size={14} /></button>
          </div>

          <aside className="lg:col-span-5">
            <div className="rp-card p-8 sticky top-24" data-testid="cost-result">
              <div className="overline">Your exposure</div>
              {!result && <p className="text-sm text-slate-500 mt-4">Select regimes, severity and turnover to estimate your worst-case penalty exposure.</p>}
              {result && (
                <>
                  <div className="font-display text-5xl font-bold mt-3" style={{ color: "var(--rp-accent)" }}>{fmt(result.total_exposure_sgd)}</div>
                  <div className="text-xs text-slate-500 mt-2 font-mono">total · severity {result.severity}</div>
                  <div className="mt-6 space-y-3">
                    {result.rows.map(r => (
                      <div key={r.id} className="border-t border-slate-200 pt-3">
                        <div className="text-sm font-medium">{r.label}</div>
                        <div className="font-mono text-base mt-1" style={{ color: "var(--rp-accent)" }}>{fmt(r.estimated_exposure_sgd)}</div>
                        <div className="text-[10px] text-slate-500 font-mono">ceiling {fmt(r.ceiling)}</div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-6 leading-relaxed flex gap-2"><AlertTriangle size={12} className="flex-shrink-0 mt-0.5" /> Indicative only — actual penalties depend on facts, mitigation and regulator discretion.</p>
                </>
              )}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

// ============ MAS Deadline Calendar ============
export function MASCalendar() {
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const { REACT_APP_BACKEND_URL } = process.env;

  useEffect(() => { api.get("/mas-calendar/deadlines").then(r => setItems(r.data.deadlines)); }, []);

  const cats = Array.from(new Set(items.map(i => i.category)));
  const filtered = cat === "all" ? items : items.filter(i => i.category === cat);

  const icsUrl = `${REACT_APP_BACKEND_URL}/api/mas-calendar/ics${cat === "all" ? "" : `?category=${encodeURIComponent(cat)}`}`;

  const critColor = { high: "#EF4444", medium: "#F59E0B", low: "#10B981" };

  return (
    <div data-testid="tool-calendar">
      <ToolHero overline="MAS Regulatory Deadline Calendar" title="Every MAS, PDPC and CSA deadline." sub="Filter by category. Download as .ics for Outlook / Google / Apple Calendar." />
      <section className="section">
        <div className="container-rp">
          <div className="flex flex-wrap items-center gap-2 mb-8">
            <button onClick={() => setCat("all")} data-active={cat === "all"} className="persona-btn" data-testid="cal-filter-all">All</button>
            {cats.map(c => (
              <button key={c} onClick={() => setCat(c)} data-active={cat === c} className="persona-btn">{c}</button>
            ))}
            <a href={icsUrl} className="btn-accent ml-auto" data-testid="cal-download-ics"><Download size={14} /> Download .ics</a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {filtered.map((d) => (
              <div key={`${d.date}-${d.title}`} className="rp-surface p-6 relative">
                <div className="absolute top-0 left-0 w-1 h-full" style={{ background: critColor[d.criticality] }} />
                <div className="overline-muted flex items-center gap-2"><CalIcon size={11} /> {d.date}</div>
                <div className="font-display text-lg font-semibold mt-2 leading-tight">{d.title}</div>
                <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                  <span className="font-mono px-2 py-0.5 rounded-sm border border-slate-200">{d.regulator}</span>
                  <span>{d.category}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
